import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLiveRefresh, useDebouncedCallback } from '@byscott-io/corebyscott';
import { useWaldoEvent } from '@byscott-io/where-is-waldo';
import { apps } from '../api/client';

export default function ClientAccountsListPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [appResponse, clientAccountsResponse] = await Promise.all([
        apps.get(appId),
        apps.clientAccounts(appId),
      ]);
      setApp(appResponse.data.app);
      setClientAccounts(clientAccountsResponse.data.client_accounts);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load client accounts');
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Live list (#626): client accounts + their subscriptions update in place.
  const refresh = useLiveRefresh(useDebouncedCallback(loadData, 500));
  useWaldoEvent(
    [
      "client_account_created", "client_account_update", "client_account_destroyed",
      "subscription_created", "subscription_update", "subscription_destroyed",
    ],
    refresh
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-500">Loading client accounts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="mb-6 text-sm">
        <Link to="/app/apps" className="text-blue-600 hover:text-blue-800">
          Apps
        </Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to={`/app/apps/${appId}`} className="text-blue-600 hover:text-blue-800">
          {app?.name}
        </Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900">Client Accounts</span>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Client Accounts</h1>
        <p className="text-gray-500 mt-1">
          Organizations registered through the client API for {app?.name}
        </p>
      </div>

      {/* Client Accounts Table */}
      <div className="bg-white shadow rounded-lg">
        {clientAccounts.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <p>No client accounts yet.</p>
            <p className="text-sm mt-2">
              Client accounts are created when your app calls the client API.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    External ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {clientAccounts.map((account) => (
                  <tr
                    key={account.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/app/apps/${appId}/clients/${account.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {account.external_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.name || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {account.email || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(account.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
